import { Badge, makeStyles, tokens, type BadgeProps } from '@fluentui/react-components';
import type { RequestStatus } from '@pr-app/shared';

/**
 * The request status, as a dot **and** a word.
 *
 * Wireframe sheet 08.1 (S2a–S2c), note 1: "High contrast has no colour at all.
 * Every status badge still reads, because each carries a word and a dot outline
 * — never a colour alone." The word is what survives; the colour is an
 * accelerant for the two themes that have one, and the outline keeps the pill
 * visible once `colorPalette*Background1` collapses to pure black.
 *
 * Labels and colours are sheet 01 (R1): Draft grey, Pending review amber,
 * Rejected red, Approved green, PR created purple.
 */
const STATUS_LABELS: Record<RequestStatus, string> = {
  DRAFT: 'Draft',
  PENDING_REVIEW: 'Pending review',
  REJECTED: 'Rejected',
  APPROVED: 'Approved',
  PR_CREATED: 'PR created',
};

const STATUS_COLORS: Record<RequestStatus, BadgeProps['color']> = {
  DRAFT: 'informative',
  PENDING_REVIEW: 'warning',
  REJECTED: 'danger',
  APPROVED: 'success',
  PR_CREATED: 'brand',
};

const useStyles = makeStyles({
  // `currentColor` rather than a palette token: the dot then tracks whatever
  // the badge's colour resolves to, including plain white in high contrast.
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: 'currentColor',
  },
});

export function statusLabel(status: RequestStatus): string {
  return STATUS_LABELS[status];
}

export function StatusBadge({ status }: { status: RequestStatus }) {
  const styles = useStyles();

  return (
    <Badge
      appearance="outline"
      color={STATUS_COLORS[status]}
      size="medium"
      // Decorative: the word beside it already carries the meaning, so
      // announcing the dot would only repeat it.
      icon={<span aria-hidden="true" className={styles.dot} />}
    >
      {STATUS_LABELS[status]}
    </Badge>
  );
}
